// API base. In the Capacitor Android build the webview is served from a
// file:// / capacitor:// origin with no same-origin backend, so the build
// is compiled with VITE_API_BASE=https://alpha.58888ai.com/api pointing at
// production. In the browser dev server it falls back to the relative '/api'
// (proxied by vite / served same-origin), so web preview keeps working.
const BASE = (import.meta.env.VITE_API_BASE as string) || '/api';

function getToken(): string {
  return localStorage.getItem('alpha_token') || localStorage.getItem('token') || '';
}

export function setToken(token: string) {
  localStorage.setItem('alpha_token', token);
  // Old code paths used the bare 'token' key; remove it on a fresh login
  // so a stale legacy token can't be picked up by getToken() after logout.
  localStorage.removeItem('token');
}

export function clearToken() {
  localStorage.removeItem('alpha_token');
  localStorage.removeItem('token');
}

export class RateLimitError extends Error {
  status = 429;
  retryAfter: number;

  constructor(retryAfterSeconds: number) {
    super("Rate limited");
    this.retryAfter = retryAfterSeconds;
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  if (res.status === 429) {
    const retryAfter = parseInt(res.headers.get("Retry-After") || "60", 10);
    // 先读 body，提取登录锁定的额外字段
    const text429 = await res.text();
    let json429: any = null;
    try { json429 = JSON.parse(text429); } catch {}
    const err: any = new RateLimitError(retryAfter);
    if (json429?.lockSeconds) err.lockSeconds = json429.lockSeconds;
    if (json429?.remainingAttempts !== undefined) err.remainingAttempts = json429.remainingAttempts;
    if (json429?.message) err.message = json429.message;
    throw err;
  }

  if (res.status === 401) {
    // 在登录页面时，让调用方自行处理错误显示
    if (window.location.pathname === "/login") {
      const text = await res.text();
      let data: any = null;
      try { data = JSON.parse(text); } catch { /* ignore */ }
      const err: any = new Error(data?.message || "手机号或密码错误");
      if (data?.remainingAttempts !== undefined) err.remainingAttempts = data.remainingAttempts;
      if (data?.lockSeconds !== undefined) err.lockSeconds = data.lockSeconds;
      throw err;
    }
    clearToken();
    window.location.href = "/login";
    throw new Error("Unauthorized");
  }

  // Read the body once and parse defensively. Reverse proxies (nginx, CF)
  // routinely return HTML on 502/504/maintenance, an empty body on 401,
  // or plain text on a network blip — calling res.json() blindly throws
  // SyntaxError that bubbles through every hook with a useless message
  // ("Unexpected token < in JSON…").
  const text = await res.text();
  let json: { success?: boolean; data?: unknown; message?: string } | null = null;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = null;
    }
  }

  if (!res.ok) {
    // Intercept 403 alpha_subscription_required -> trigger sim lock overlay.
    if (res.status === 403 && (json as any)?.code === "alpha_subscription_required") {
      window.dispatchEvent(new CustomEvent("sim-lock"));
    }
    const msg = json?.message || `HTTP ${res.status}` + (text ? `: ${text.slice(0, 120)}` : '');
    var err: any = new Error(msg); if ((json as any)?.remainingAttempts !== undefined) err.remainingAttempts = (json as any).remainingAttempts; if ((json as any)?.lockSeconds !== undefined) err.lockSeconds = (json as any).lockSeconds; throw err;
  }
  if (!json || json.success === false) {
    throw new Error(json?.message || 'request failed');
  }

  return json.data as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
